/* eslint-disable react/no-unescaped-entities */
// import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useLocation,
  isRouteErrorResponse,
  Link,
  type LinksFunction,
} from "react-router";
import stylesheet from "./tailwind.css?url";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  // dark is anything not /
  const isDark = location.pathname !== "/";

  return (
    <html lang="en" className={isDark ? "dark" : ""}>
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  // captureRemixErrorBoundaryError(error);

  console.error(error);

  // Handle 404 errors
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <html lang="en" className="dark">
        <head>
          <meta charSet="utf-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
          />
          <title>Page Not Found | PunchPocket</title>
          <Meta />
          <Links />
        </head>
        <body className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 min-h-screen flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <div className="text-4xl">🔍</div>
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">
                Page Not Found
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                We couldn't find the page you're looking for. It may have been
                moved or doesn't exist.
              </p>
            </div>

            <div className="space-y-4">
              <Link
                to="/"
                className="inline-block bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
              >
                Go to Homepage
              </Link>

              <div className="text-sm text-gray-400">
                <p>
                  Need help?{" "}
                  <a
                    href="https://wa.me/447792761737"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Contact our support team
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
          <Scripts />
        </body>
      </html>
    );
  }

  // Handle 5xx server errors
  if (isRouteErrorResponse(error) && error.status >= 500) {
    return (
      <html lang="en" className="dark">
        <head>
          <meta charSet="utf-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
          />
          <title>Something Went Wrong | PunchPocket</title>
          <Meta />
          <Links />
        </head>
        <body className="bg-gradient-to-br from-gray-900 via-red-900/20 to-gray-900 min-h-screen flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                <div className="text-4xl">⚠️</div>
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">
                Server Error
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                We're experiencing technical difficulties. Our team has been
                notified and is working to resolve this issue.
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => window.location.reload()}
                className="inline-block bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-red-700 transition-all duration-200 transform hover:scale-105"
              >
                Refresh Page
              </button>

              <div className="text-sm text-gray-400">
                <p>
                  If the problem persists, please{" "}
                  <a
                    href="https://wa.me/447792761737"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-400 hover:text-orange-300 underline"
                  >
                    contact support
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
          <Scripts />
        </body>
      </html>
    );
  }

  // Handle other errors (client errors, etc.)
  return (
    <html lang="en" className="dark">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
        <title>Unexpected Error | PunchPocket</title>
        <Meta />
        <Links />
      </head>
      <body className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
              <div className="text-4xl">⚙️</div>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              Unexpected Error
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Something went wrong. Please try refreshing the page or return to
              the homepage.
            </p>
          </div>

          <div className="space-y-4">
            <Link
              to="/"
              className="inline-block bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-yellow-600 hover:to-orange-700 transition-all duration-200 transform hover:scale-105"
            >
              Go to Homepage
            </Link>

            <div className="text-sm text-gray-400">
              <p>
                Error persisting?{" "}
                <a
                  href="https://wa.me/447792761737"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400 hover:text-yellow-300 underline"
                >
                  Contact our support team
                </a>{" "}
                for help.
              </p>
            </div>
          </div>
        </div>
        <Scripts />
      </body>
    </html>
  );
};

export default function App() {
  return <Outlet />;
}
